import { useEffect, useRef, useState } from "react";
import { MapPin, AlertTriangle } from "lucide-react";

const BROWSER_KEY = process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY;
let loader = null;

const loadMaps = () => {
  if (window.google?.maps) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${BROWSER_KEY}&v=weekly`;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => { loader = null; reject(new Error("maps")); };
    document.head.appendChild(s);
  });
  return loader;
};

// Small fixed-height map: the broker confirms or drags the pin to the real spot.
export const PinMap = ({ lang, lat, lng, onMove, adjusted }) => {
  const el = useRef(null);
  const marker = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!BROWSER_KEY || lat == null || lng == null) return;
    let cancelled = false;
    loadMaps().then(() => {
      if (cancelled || !el.current) return;
      const pos = { lat, lng };
      const map = new window.google.maps.Map(el.current, {
        center: pos, zoom: 19, mapTypeId: "satellite",
        mapTypeControl: true, streetViewControl: false, fullscreenControl: false,
        gestureHandling: "greedy",
        mapTypeControlOptions: { mapTypeIds: ["roadmap", "satellite"] },
      });
      marker.current = new window.google.maps.Marker({ map, position: pos, draggable: true });
      marker.current.addListener("dragend", (e) => onMove({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
      map.addListener("click", (e) => {
        marker.current.setPosition(e.latLng);
        onMove({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });
    }).catch(() => setFailed(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (marker.current && lat != null && lng != null) marker.current.setPosition({ lat, lng });
  }, [lat, lng]);

  if (!BROWSER_KEY || failed) {
    return (
      <div className="mt-3 text-xs text-[#E6412D] flex items-center gap-2" data-testid="pin-map-unavailable">
        <AlertTriangle className="w-3.5 h-3.5"/>
        {lang === "fr" ? "Carte indisponible : clé Google du navigateur manquante." : "Map unavailable: browser Google key missing."}
      </div>
    );
  }
  return (
    <div className="mt-3" data-testid="pin-map">
      <div className="text-xs text-zinc-400 flex items-start gap-1.5 mb-2">
        <MapPin className="w-3.5 h-3.5 text-[#D8301C] mt-0.5 shrink-0"/>
        <span>{lang === "fr"
          ? "La propriété est-elle bien ici ? Sinon, déplacez l'épingle au bon endroit."
          : "Is the property right here? If not, drag the pin to the right spot."}</span>
      </div>
      <div ref={el} className="w-full rounded-lg border border-[rgba(216,48,28,0.35)] overflow-hidden"
        style={{ height: 220 }} data-testid="pin-map-canvas"/>
      {adjusted && (
        <div className="text-xs text-emerald-400 mt-2" data-testid="pin-adjusted">
          {lang === "fr" ? "Emplacement ajusté. Le trajet et les frais partiront de cette épingle." : "Location adjusted. Travel and fees will use this pin."}
        </div>
      )}
    </div>
  );
};
